/**
 * Pure geometry for the board's hex tokens (`projects/hex-tokens.md`). Every token is a pointy-top
 * hex inscribed in its square footprint, and its vitals are trim bands just outside the hex edges.
 * No Pixi here; `pixiTokenSprite.ts`, `pixiTokenChrome.ts` and `pixiCombatBoard.ts` fill and stroke
 * what this returns.
 *
 * Radii are circumradii (centre to vertex). {@link growHex} converts a distance measured along an
 * edge's normal into a circumradius, so "3 px outside the ring" means 3 px off every edge, which is
 * also how Pixi's mitred strokes grow a hex.
 */

export const COS30 = Math.cos(Math.PI / 6);

export type Pt = readonly [number, number];

/** Vertex indices, clockwise from the top peak. */
export const HEX = { top: 0, upperRight: 1, lowerRight: 2, bottom: 3, lowerLeft: 4, upperLeft: 5 } as const;

/** Vertex `k` of the hex of circumradius `r` centred at `(c, c)`, a square footprint's centre. */
export function hexVertex(c: number, r: number, k: number): Pt {
  const a = ((-90 + 60 * k) * Math.PI) / 180;
  return [c + r * Math.cos(a), c + r * Math.sin(a)];
}

/** The six vertices about `(cx, cy)` as flat `[x, y, …]`, for `Graphics.poly(points, true)`. Pixi v8
 *  leaves a poly open unless `close` is passed, and a stroke then loses its last edge. */
export function hexPoints(cx: number, cy: number, r: number): number[] {
  const out: number[] = [];
  for (let k = 0; k < 6; k++) {
    const [x, y] = hexVertex(0, r, k);
    out.push(cx + x, cy + y);
  }
  return out;
}

/** The circumradius whose edges sit `d` px outside (or inside, for negative `d`) a hex of `r`. */
export const growHex = (r: number, d: number): number => r + d / COS30;

const lerp = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

/**
 * The quad of the band between radii `rin` and `rout` along the edge from vertex `from` to vertex
 * `to`, from `t0` to `t1` of the edge's length. At `t = 0` or `1` the quad's end is the corner's
 * bisector, so two bands on adjacent edges meet mitred at the hex's own angle.
 */
export function bandQuad(c: number, band: { rin: number; rout: number }, edge: { from: number; to: number; t0: number; t1: number }): number[] {
  const ia = hexVertex(c, band.rin, edge.from);
  const ib = hexVertex(c, band.rin, edge.to);
  const oa = hexVertex(c, band.rout, edge.from);
  const ob = hexVertex(c, band.rout, edge.to);
  return [lerp(ia, ib, edge.t0), lerp(ia, ib, edge.t1), lerp(oa, ob, edge.t1), lerp(oa, ob, edge.t0)].flat();
}

/**
 * Split `frac` (0..1) of a run of consecutive edges (a vertex path such as `[lowerLeft, bottom,
 * lowerRight]`) into per-edge spans, filling from the path's start. An empty span is dropped.
 */
export function runSpans(path: readonly number[], frac: number): { from: number; to: number; t0: number; t1: number }[] {
  const edges = path.length - 1;
  let remain = Math.max(0, Math.min(1, frac)) * edges;
  const out: { from: number; to: number; t0: number; t1: number }[] = [];
  for (let i = 0; i < edges && remain > 0; i++) {
    const t1 = Math.min(1, remain);
    out.push({ from: path[i]!, to: path[i + 1]!, t0: 0, t1 });
    remain -= t1;
  }
  return out;
}

/** A dashed hex outline as `[x1, y1, x2, y2]` segments, dashes restarting at each vertex. */
export function dashedHex(c: number, r: number, pattern: { dash: number; gap: number }): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  for (let k = 0; k < 6; k++) {
    const [x1, y1] = hexVertex(c, r, k);
    const [x2, y2] = hexVertex(c, r, (k + 1) % 6);
    const len = Math.hypot(x2 - x1, y2 - y1);
    const ux = (x2 - x1) / len;
    const uy = (y2 - y1) / len;
    for (let pos = 0; pos < len; pos += pattern.dash + pattern.gap) {
      const end = Math.min(pos + pattern.dash, len);
      out.push([x1 + ux * pos, y1 + uy * pos, x1 + ux * end, y1 + uy * end]);
    }
  }
  return out;
}

/**
 * How far a token's drawing (hex, trim and peak mark) spans relative to its footprint: the trim's
 * outer circumradius is this × half the side. Size 1 draws at 1.2× the cell and overflows it a
 * little; Size 2+ at 1.08× (`decisions/0197` as amended 2026-09-29: "make the whole thing just a bit
 * bigger, letting 1x1's overflow their tile a bit"). Hit-testing, footprint and rules ignore it.
 */
export const TOKEN_SCALE = { small: 1.2, big: 1.08 } as const;
/** Trim thickness along an edge's normal at scale 1: Size 1, then Size 2+. */
const TRIM_SMALL = 2.5;
const TRIM_BIG = 3.5;
/** Gap between the ring's outer edge and the trim. */
const TRIM_GAP = 0.8;
/** The trim's dark under-band reaches this far past each side of the band, so it reads on light
 *  ground. */
export const TRIM_OUTLINE = 0.9;

/** One token's hex layout in footprint-local pixels. */
export type TokenHex = {
  /** The centre on both axes (`side / 2`). */
  c: number;
  /** The ring's OUTER edge; the ring's centreline sits `ringWidth / 2` inside it. */
  ringOuter: number;
  trimIn: number;
  trimOut: number;
  /** The trim thickness, for glow and pad sizes. */
  trim: number;
};

/**
 * Laid out from the outside in, about the footprint's centre: the trim's outer circumradius is
 * {@link TOKEN_SCALE} × half the side, so its top peak and bottom point sit past the footprint's
 * edges; the trim is {@link TRIM_SMALL} / {@link TRIM_BIG} thick scaled the same, then
 * {@link TRIM_GAP}, then the ring. `cell` decides Size 1 scale; a 0.5 footprint renders as a full
 * cell too.
 */
export function tokenHex(side: number, cell: number): TokenHex {
  const c = side / 2;
  const scale = side <= cell ? TOKEN_SCALE.small : TOKEN_SCALE.big;
  const trim = (side <= cell ? TRIM_SMALL : TRIM_BIG) * scale;
  const trimOut = c * scale;
  const trimIn = growHex(trimOut, -trim);
  return { c, ringOuter: growHex(trimIn, -TRIM_GAP), trimIn, trimOut, trim };
}

/** The hex's top peak, where the turn mark sits (and turn-presence's ACTIVE tag). */
export function peakAnchor(hex: TokenHex): { x: number; y: number } {
  return { x: hex.c, y: hex.c - hex.trimOut };
}

/**
 * How far the token's drawing, trim outline included, reaches past each side of its footprint:
 * `y` past the top and bottom edges (the peak and the bottom point), `x` past the left and right
 * (the flat sides), never negative. The label solver pads the token rect by it.
 */
export function tokenOverflow(side: number, cell: number): { x: number; y: number } {
  const hex = tokenHex(side, cell);
  const r = growHex(hex.trimOut, TRIM_OUTLINE);
  return { x: Math.max(0, r * COS30 - hex.c), y: Math.max(0, r - hex.c) };
}

/** The circumradius of the selection halo and the target rings: a tenth of the side outside the trim. */
export function markRingRadius(side: number, cell: number): number {
  return tokenHex(side, cell).trimOut + side * 0.1;
}

/**
 * The lowest y a box `w` wide, centred on the token, can reach without touching the lower trim or
 * its outline: its bottom corners sit on the inner edge of the two lower edges' band. A Size 2+
 * token's inside name label sits here (`labelLayout.ts`'s `LabelItem.insideBottom`).
 */
export function insideLabelFloor(hex: TokenHex, w: number): number {
  const rin = growHex(hex.trimIn, -TRIM_OUTLINE);
  // The lower edges fall at 30° from the lower corners to the bottom point at `c + rin`.
  return hex.c + rin - (w / 2) * Math.tan(Math.PI / 6);
}
